/**
 * MIPS payment-adjustment math — SINGLE implementation (backlog R10, extracted
 * 2026-08-03 from six byte-identical copies in PaymentAdjustmentReport,
 * PaymentAdjustmentCard, PaymentAdjustmentModal, UnifiedScoreCard,
 * ImprovementOpportunity, PaymentCurveChart).
 *
 * Medium scenario ONLY — that is what every live UI computes today. Spec:
 * docs/reference/payment-adjustment.md.
 *
 * NOT for APP Plus: the APP exposure model (src/lib/app-plus/years/<year>/exposure.ts)
 * uses a DIFFERENT CMS formula (18.75 floor, no bonus %) and must never import
 * this module. Reconciling the two is a product decision, not housekeeping.
 */

/** Maximum MIPS penalty as a decimal fraction (-9%). */
export const MAX_PENALTY = -0.09;
const PENALTY_REDUCTION_PER_POINT = 0.09 / 57;
const NEUTRAL_SCORE = 75;
const BONUS_ZONE_POINTS = 25;
const MEDIUM_MULTIPLIER = 1.25;

/**
 * Score -> payment adjustment (decimal fraction, e.g. -0.09 = -9%).
 * Callers pass an INTEGER score 0-100 (round/clamp at the call site, exactly
 * as the original copies did — this function is a mechanical extract and adds
 * no input normalization).
 *
 *   <=18   : -9% (maximum penalty)
 *   19-74  : graduated penalty, linear from -9% to ~0%
 *   75     : neutral (0%)
 *   >75    : Medium-scenario bonus — ((low x 1.25)/100) x scalingFactor / 25
 *            per point above 75
 */
export function computeAdjustmentPct(
  score: number,
  lowScenarioInput: number,
  scalingFactor: number = 0.74,
): number {
  if (score >= 0 && score <= 18) {
    return MAX_PENALTY;
  } else if (score >= 19 && score <= 74) {
    const pointsAboveThreshold = score - 18;
    return MAX_PENALTY + (pointsAboveThreshold * PENALTY_REDUCTION_PER_POINT);
  } else if (score === 75) {
    return 0;
  } else {
    const mediumModeledAdjustment = (lowScenarioInput * MEDIUM_MULTIPLIER) / 100;
    const actualMaxAtScore100 = mediumModeledAdjustment * scalingFactor;
    const perPointBonus = actualMaxAtScore100 / BONUS_ZONE_POINTS;
    const pointsAboveNeutral = score - NEUTRAL_SCORE;
    return pointsAboveNeutral * perPointBonus;
  }
}

/** Title-case zone label (shared by the Report and Modal UIs). */
export function determineZone(score: number): string {
  if (score <= 18) return 'Maximum Penalty';
  if (score <= 74) return 'Graduated Penalty';
  if (score === 75) return 'Neutral';
  return 'Bonus';
}

/** Payment year for a performance year (CMS applies adjustments 2 years later). */
export function paymentYearFor(performanceYear: number, offset: number = 2): number {
  return performanceYear + offset;
}
